import { Lightbulb } from "lucide-react";
import type { WidgetMessengerConfig } from "@/lib/widget-config";
import { Typewriter } from "./typewriter";

/**
 * Renders one operator-configured welcome message at the top of the
 * messenger thread.
 *
 * Two kinds, two layouts:
 *   - **HINT**   — centred dim italic strip. Reads as a tip /
 *                  hint, not a message — same visual weight as
 *                  the home-tab "Usually replies in a few minutes"
 *                  footer.
 *   - **SYSTEM** — centred pill on a muted surface. Matches the
 *                  runtime SYSTEM messages the BE emits during
 *                  handoffs / status changes.
 *
 * (`TEXT` was removed when the BE landed the WEB_VISITOR_VISIT flow
 * trigger — proactive bot bubbles now arrive via SSE as runtime
 * messages, not as static config. The `MessageBubble` AI branch is
 * the source of truth for that look.)
 */
export function WelcomeMessage({
  kind,
  text,
  typewriter = false,
  typewriterDelayMs = 0,
}: {
  kind: WidgetMessengerConfig["messages"][number]["kind"];
  text: string;
  /** SYSTEM: reveal the body character-by-character with a blinking
   *  caret. Off → renders whole. HINT ignores the flag — tips don't
   *  type in. */
  typewriter?: boolean;
  /** When `typewriter`: hold a "typing…" beat (bouncing dots) for this
   *  long before the first character appears. 0 → type immediately. */
  typewriterDelayMs?: number;
}) {
  if (kind === "SYSTEM") {
    return (
      <div className="flex justify-center px-2 py-1">
        <span className="text-[11px] text-wx-fg-muted">
          {typewriter ? <Typewriter text={text} startDelayMs={typewriterDelayMs} /> : text}
        </span>
      </div>
    );
  }
  // HINT — full-width, left-aligned tip card. Icon pinned to the top
  // (`items-start` + `mt-0.5`) so it stays beside the first line when
  // the text wraps to multiple lines.
  return (
    <div className="flex w-full items-start gap-2 rounded-wx-sm border border-wx-border bg-wx-bg px-3 py-2.5 text-wx-fg-muted">
      <Lightbulb size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
      <p className="text-xs leading-relaxed italic">{text}</p>
    </div>
  );
}
